import type {
    PrivateInviteChannels,
    PrivateInviteStatus,
    PrivateInviteVerificationRemark,
} from "@vesta/vos-types/modules/workspaceLevel/privateInvites/domain/PrivateInvite";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {z as zod} from "zod";
import {dataResult, type Result} from "@vesta/error-handling-remix/result";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import type {UserStatus} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {NotificationType} from "@vesta/vos-types/modules/platformLevel/notifications/domain/Notification";

export async function updatePlatformUserDetails({
    accessToken,
    email,
    name,
    phoneNumber,
    intellsysBaseUrl,
}: {
    accessToken: string;
    email: string;
    name: string;
    phoneNumber: string;
    intellsysBaseUrl: string;
}): Promise<Result<unknown>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/user-details/upsert`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
                userDetails: {
                    email: email.trim() || null,
                    name,
                    phoneNumber: phoneNumber.trim() || null,
                },
            }),
        },
        zod.unknown(),
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult(result.data);
}

export async function createWorkspaceNotification({
    workspaceId,
    header,
    content,
    type,
    additionalData = {},
    intellsysBaseUrl,
    accessToken,
    userId,
}: {
    workspaceId: Uuid;
    header: string;
    content: string;
    type: NotificationType;
    additionalData?: Record<string, unknown>;
    intellsysBaseUrl: string;
    accessToken: string;
    userId: Uuid;
}): Promise<Result<unknown>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/notifications/create`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify({
                notification: {
                    additionalData: additionalData,
                    content: content,
                    header: header,
                    type: type,
                    workspaceId: workspaceId,
                    userId: userId,
                },
            }),
        },
        zod.unknown(),
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult(result.data);
}

// TODO: Fix return type to be more specific than unknown
export async function createWorkspacePrivateInvite({
    workspaceId,
    userTemplateId,
    expiresAt,
    status,
    allowedEmail,
    allowedPhoneNumber,
    inviteChannels,
    fixedAdditionalColumnValues,
    inviteFormData,
    parentUserId,
    verificationRemark,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    userTemplateId: Uuid;
    expiresAt: string;
    status: Uuid;
    allowedEmail: string;
    allowedPhoneNumber: string;
    inviteChannels: PrivateInviteChannels;
    fixedAdditionalColumnValues: Record<Uuid, unknown>;
    inviteFormData: Record<Uuid, unknown> | null;
    parentUserId: Uuid | null;
    verificationRemark: PrivateInviteVerificationRemark | null;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<unknown>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/private-invites/create`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify({
                inviteChannels: inviteChannels,
                privateInvite: {
                    userTemplateId: userTemplateId,
                    status: status,
                    allowedEmail: allowedEmail,
                    allowedPhoneNumber: allowedPhoneNumber,
                    fixedAdditionalColumnValues: fixedAdditionalColumnValues,
                    inviteFormData: inviteFormData,
                    parentUserId: parentUserId,
                    verificationRemark: verificationRemark,
                    expiresAt: expiresAt,
                    workspaceId: workspaceId,
                },
            }),
        },
        zod.unknown(),
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult(result.data);
}

export async function bulkCreateWorkspacePrivateInvites({
    workspaceId,
    privateInvites,
    inviteChannels,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    privateInvites: Array<Omit<Parameters<typeof createWorkspacePrivateInvite>[0], "accessToken" | "intellsysBaseUrl" | "inviteChannels">>;
    inviteChannels: PrivateInviteChannels;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<unknown>> {
    return await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/private-invites/bulk-create`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify({
                privateInvites: privateInvites.map((privateInvite) => ({
                    inviteChannels: inviteChannels,
                    privateInvite: privateInvite,
                })),
            }),
        },
        zod.unknown(),
    );
}

export async function updateWorkspacePrivateInvite({
    workspaceId,
    id,
    userTemplateId,
    status,
    allowedEmail,
    allowedPhoneNumber,
    fixedAdditionalColumnValues,
    inviteFormData,
    parentUserId,
    verificationRemark,
    intellsysBaseUrl,
    accessToken,
}: {
    workspaceId: Uuid;
    id: Uuid;
    userTemplateId?: Uuid;
    status?: PrivateInviteStatus;
    allowedEmail?: string;
    allowedPhoneNumber?: string;
    fixedAdditionalColumnValues?: Record<Uuid, unknown>;
    inviteFormData?: Record<Uuid, unknown> | null;
    parentUserId?: Uuid | null;
    verificationRemark?: PrivateInviteVerificationRemark | null;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<unknown>> {
    const privateInvite: Record<string, unknown> = {id: id};
    if (allowedEmail !== undefined) {
        privateInvite.allowedEmail = allowedEmail;
    }
    if (allowedPhoneNumber !== undefined) {
        privateInvite.allowedPhoneNumber = allowedPhoneNumber;
    }
    if (fixedAdditionalColumnValues !== undefined) {
        privateInvite.fixedAdditionalColumnValues = fixedAdditionalColumnValues;
    }
    if (inviteFormData !== undefined) {
        privateInvite.inviteFormData = inviteFormData;
    }
    if (parentUserId !== undefined) {
        privateInvite.parentUserId = parentUserId;
    }
    if (status !== undefined) {
        privateInvite.status = status;
    }
    if (userTemplateId !== undefined) {
        privateInvite.userTemplateId = userTemplateId;
    }
    if (verificationRemark !== undefined) {
        privateInvite.verificationRemark = verificationRemark;
    }

    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/private-invites/update`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify({
                privateInvite: privateInvite,
            }),
        },
        zod.unknown(),
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult(result.data);
}

export async function submitWorkspacePrivateInviteForm({
    privateInviteId,
    inviteCode,
    inviteFormData,
    intellsysBaseUrl,
    accessToken,
}: {
    privateInviteId: Uuid;
    inviteCode: string;
    inviteFormData: Record<Uuid, unknown>;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<unknown>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/private-invites/submit-form`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
                privateInviteId: privateInviteId,
                inviteCode: inviteCode,
                inviteFormData: inviteFormData,
            }),
        },
        zod.unknown(),
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult(result.data);
}

export async function redeemWorkspacePrivateInvite({
    privateInviteId,
    inviteCode,
    intellsysBaseUrl,
    accessToken,
}: {
    privateInviteId: Uuid;
    inviteCode: string;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Result<unknown>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/private-invites/redeem`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
                privateInviteId: privateInviteId,
                inviteCode: inviteCode,
            }),
        },
        zod.unknown(),
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult(result.data);
}

export async function createWorkspaceUser({
    additionalColumnValues,
    parentId,
    templateId,
    userId,
    status,
    accessToken,
    workspaceId,
    intellsysBaseUrl,
}: {
    additionalColumnValues: Record<Uuid, unknown>;
    parentId: Uuid | null | undefined;
    templateId: Uuid;
    userId: Uuid;
    status?: UserStatus;
    accessToken: string;
    workspaceId: Uuid;
    intellsysBaseUrl: string;
}): Promise<Result<unknown>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/users/create`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify({
                user: {
                    additionalColumnValues: additionalColumnValues,
                    userId: userId,
                    parentId: parentId ?? null,
                    templateId: templateId,
                    status: status,
                },
            }),
        },
        zod.unknown(),
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult(result.data);
}

export async function createAdminWorkspaceUser({
    additionalColumnValues,
    parentId,
    templateId,
    userId,
    status,
    accessToken,
    workspaceId,
    intellsysBaseUrl,
}: {
    additionalColumnValues: Record<Uuid, unknown>;
    parentId: Uuid | null | undefined;
    templateId: Uuid;
    userId: Uuid;
    status?: UserStatus;
    accessToken: string;
    workspaceId: Uuid;
    intellsysBaseUrl: string;
}): Promise<Result<unknown>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/admin/users/create`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                user: {
                    additionalColumnValues: additionalColumnValues,
                    userId: userId,
                    parentId: parentId ?? null,
                    templateId: templateId,
                    status: status,
                },
                workspaceId: workspaceId,
            }),
        },
        zod.unknown(),
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult(result.data);
}

export async function updateWorkspaceUser({
    accessToken,
    additionalColumnValues,
    userId,
    parentId,
    templateId,
    workspaceId,
    intellsysBaseUrl,
    status,
}: {
    additionalColumnValues: Record<Uuid, unknown>;
    userId: Uuid;
    parentId: Uuid | null;
    templateId: Uuid;
    accessToken: string;
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    status: UserStatus;
}): Promise<Result<unknown>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/users/update`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify({
                user: {
                    additionalColumnValues: additionalColumnValues,
                    status: status,
                    parentId: parentId,
                    templateId: templateId,
                    userId: userId,
                },
            }),
        },
        zod.unknown(),
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult(result.data);
}

export async function bulkUpdateWorkspaceUsers({
    accessToken,
    intellsysBaseUrl,
    status,
    userIds,
    workspaceId,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    status: UserStatus;
    userIds: Array<Uuid>;
    workspaceId: Uuid;
}): Promise<Result<unknown>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/users/bulk-update`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify({
                updates: {
                    status: status,
                },
                userIds: userIds,
            }),
        },
        zod.unknown(),
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult(result.data);
}

export async function updateSelfWorkspaceUser({
    accessToken,
    additionalColumnValues,
    templateId,
    workspaceId,
    intellsysBaseUrl,
}: {
    additionalColumnValues: Record<Uuid, unknown>;
    templateId: Uuid;
    accessToken: string;
    workspaceId: Uuid;
    intellsysBaseUrl: string;
}): Promise<Result<unknown>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/users/update-self`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
                additionalColumnValues: additionalColumnValues,
                templateId: templateId,
                workspaceId: workspaceId,
            }),
        },
        zod.unknown(),
    );
    if (result.success !== true) {
        return result;
    }
    return dataResult(result.data);
}
